/* eslint-disable max-lines */
import React, {RefObject} from 'react';
import ReactDOM from 'react-dom';
import {inject, observer} from 'mobx-react';
import I18n from 'app/i18n';
import type UserInterface from 'app/interfaces/UserInterface';
import type {UserStore} from 'app/store/UserStore';
import {configStore} from 'app/store/global';
import {applyNoScroll} from 'app/utils/applyNoScroll';
import {revertNoScroll} from 'app/utils/revertNoScroll';
import {openAuth, openRegistration} from 'app/components/auth/auth';
import {betMoney, getLinkPath} from 'app/utils';
import {navigateGoTo} from 'app/utils/navigate/navigateGoTo';
import withRouter from 'app/utils/navigate/customWithRouter';
import type {Params} from 'react-router';
import {widgetShellStyles} from 'app/components/pages/poker/ProomPokerWidgetShell';
import AsideItem from 'site_version/com/AsideItem';
import {
    clampTablePosition,
    clampWidgetWidth,
    getDefaultTablePosition,
    WIDGET_NATURAL_WIDTH,
    type TablePosition
} from 'app/components/pages/poker/proom/proomWidgetGeometry';
import ProomTableWindowItem, {type TableWindow} from 'app/components/pages/poker/proom/ProomTableWindowItem';
import type {ProomSessionContext} from 'app/components/pages/poker/proom/proomTableTypes';
import PokerCoolOffStub from 'app/components/pages/poker/PokerCoolOffStub';
import {coolOffPoker} from 'app/utils/isCoolOffActiveFor';
import {LobbyAvailabilityWatcher} from 'app/utils/lobbyAvailability';
import {subscribeWidgetInset, widgetInset} from 'app/utils/widgetInset';
import type {ProomMessage, ProomTableInfo} from 'app/utils/proom/proomMessages';
import {
    buildLobbySrc,
    buildTableSrc,
    DESKTOP_FIT_VALUE,
    MOBILE_FIT_VALUE,
    PROOM_ORIGIN
} from 'app/utils/proom/proomUrl';
import {
    broadcastUserToken,
    getIframeElements,
    requestUserTokenRefresh
} from 'app/utils/proom/proomUserToken';
import {
    isBoardIdClaimedRemotely,
    subscribeProomTableCoordination,
    type ProomTableCoordinationMessage
} from 'app/utils/proom/proomTableCoordination';

interface Props {
    embedded?: boolean
    params?: Params
    userStore?: UserStore
}

interface State {
    lobbyHeight: number
    isMobileTableView: boolean
    lobbyUnavailable: boolean
    reloadAttempt: number
    tableWindows: Map<string, TableWindow>
}

const DEFAULT_LOBBY_HEIGHT = 600;
export const EMBEDDED_LOBBY_HEIGHT = 680;
const MOBILE_WIDGET_BREAKPOINT = 600;
const DESKTOP_WIDGET_Z_INDEX = 9000;

@inject('userStore')
@observer
class ProomPoker extends React.Component<Props, State> {
    lobbyIframeRef: RefObject<HTMLIFrameElement> = React.createRef();

    tableIframeRefs: Map<string, RefObject<HTMLIFrameElement>> = new Map();

    private zIndexCounter = DESKTOP_WIDGET_Z_INDEX;

    private readonly lobbyWatcher: LobbyAvailabilityWatcher;

    private unsubscribeTableCoordination: (() => void) | null = null;

    private unsubscribeInset: (() => void) | null = null;

    private readonly inset = widgetInset();

    private readonly initialLobbySrc: string;

    private latestUserToken: string = (window as Window & {PROOM_CONFIG?: {userToken?: string}}).PROOM_CONFIG?.userToken || '';

    constructor(props: Props) {
        super(props);
        this.initialLobbySrc = props.params?.tournamentId
            ? this.buildLobbySrc(String(props.params.tournamentId))
            : this.buildLobbySrc();

        this.state = {
            isMobileTableView: this.getIsMobileTableView(),
            lobbyHeight: props.embedded ? EMBEDDED_LOBBY_HEIGHT : DEFAULT_LOBBY_HEIGHT,
            lobbyUnavailable: false,
            reloadAttempt: 0,
            tableWindows: new Map()
        };
        this.lobbyWatcher = new LobbyAvailabilityWatcher({
            embedded: Boolean(props.embedded),
            getLobbyOrigin: () => PROOM_ORIGIN,
            lobby: 'poker',
            onRecovered: this.handleLobbyRecovered,
            onUnavailable: this.handleLobbyUnavailable
        });
    }

    componentDidMount(): void {
        window.addEventListener('message', this.handleMessage);
        window.addEventListener('resize', this.handleResize);
        this.handleResize();

        if (this.isEmbedded) {
            this.unsubscribeInset = subscribeWidgetInset(this.postInset);
        }

        if (!this.isCoolOffActive) {
            this.lobbyWatcher.start();
            this.unsubscribeTableCoordination = subscribeProomTableCoordination(this.handleTableCoordination);
        }
    }

    componentWillUnmount(): void {
        window.removeEventListener('message', this.handleMessage);
        window.removeEventListener('resize', this.handleResize);
        this.unsubscribeInset?.();
        this.unsubscribeInset = null;
        this.lobbyWatcher.stop();
        this.unsubscribeTableCoordination?.();
        this.unsubscribeTableCoordination = null;

        if (this.state.tableWindows.size > 0 && this.state.isMobileTableView) {
            revertNoScroll();
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State): void {
        const wasMobileTableOpen = prevState.isMobileTableView && prevState.tableWindows.size > 0;
        const isMobileTableOpen = this.state.isMobileTableView && this.state.tableWindows.size > 0;

        if (isMobileTableOpen && !wasMobileTableOpen) {
            applyNoScroll();
        }

        if (!isMobileTableOpen && wasMobileTableOpen) {
            revertNoScroll();
        }

        const prevTournamentId = prevProps.params?.tournamentId;
        const tournamentId = this.props.params?.tournamentId;

        if (prevTournamentId !== tournamentId && this.lobbyMode === 'full-lobby') {
            this.sendSetTournament(tournamentId);
        }
    }

    get user(): UserInterface {
        return this.props.userStore?.user;
    }

    get currentExternalId(): string {
        return String(this.user?.id || '');
    }

    get isEmbedded(): boolean {
        return Boolean(this.props.embedded);
    }

    get playerProfilesBaseUrl(): string {
        return configStore.PROOM_CONFIG.playerProfilesBaseUrl || window.location.origin;
    }

    get playerProfilesToken(): string {
        return configStore.PROOM_CONFIG.playerProfilesToken || '';
    }

    get userToken(): string {
        return configStore.PROOM_CONFIG.userToken || '';
    }

    get parentOrigin(): string {
        return configStore.PROOM_CONFIG.parentOrigin || '';
    }

    get lobbyMode(): 'lobby' | 'full-lobby' {
        return this.isEmbedded ? 'lobby' : 'full-lobby';
    }

    get isCoolOffActive(): boolean {
        return coolOffPoker(this.user);
    }

    get fitValue(): string {
        return this.getIsMobileTableView() ? MOBILE_FIT_VALUE : DESKTOP_FIT_VALUE;
    }

    buildLobbySrc = (tournamentId?: string): string => buildLobbySrc({
        externalId: this.currentExternalId,
        fit: this.fitValue,
        ...this.isEmbedded ? {inset: this.inset} : {},
        lang: I18n.locale,
        mode: this.lobbyMode,
        nickname: this.user?.name || '',
        parentOrigin: this.parentOrigin,
        playerProfilesBaseUrl: this.playerProfilesBaseUrl,
        playerProfilesToken: this.playerProfilesToken,
        tournamentId,
        userToken: this.userToken
    });

    get lobbyIframeSrc(): string {
        if (this.isEmbedded) {
            return this.buildLobbySrc();
        }

        return this.initialLobbySrc;
    }

    get balanceLabel(): string {
        const amount = this.user?.balance || this.user?.full_balance || '0';
        return betMoney({amount});
    }

    get proomSession(): ProomSessionContext {
        return {
            balanceLabel: this.balanceLabel,
            nickname: this.user?.name || '',
            parentOrigin: this.parentOrigin,
            playerProfilesBaseUrl: this.playerProfilesBaseUrl,
            playerProfilesToken: this.playerProfilesToken,
            userToken: this.latestUserToken
        };
    }

    getIsMobileTableView = (): boolean => typeof window !== 'undefined' && window.innerWidth < MOBILE_WIDGET_BREAKPOINT;

    buildTableSrc = (boardId: string, externalId: string): string => buildTableSrc({
        boardId,
        externalId,
        fit: this.fitValue,
        lang: I18n.locale,
        nickname: this.user?.name || '',
        parentOrigin: this.parentOrigin,
        playerProfilesBaseUrl: this.playerProfilesBaseUrl,
        playerProfilesToken: this.playerProfilesToken,
        userToken: this.latestUserToken
    });

    handleResize = (): void => {
        const isMobileTableView = this.getIsMobileTableView();
        this.setState(prevState => {
            if (prevState.isMobileTableView === isMobileTableView && prevState.tableWindows.size === 0) {
                return null;
            }

            const newTableWindows = new Map<string, TableWindow>();
            Array.from(prevState.tableWindows.entries()).forEach(([boardId, tw]) => {
                const width = clampWidgetWidth(tw.width);
                newTableWindows.set(boardId, {
                    ...tw,
                    position: isMobileTableView
                        ? null
                        : clampTablePosition(tw.position || getDefaultTablePosition(0, width), width),
                    width
                });
            });
            return {isMobileTableView, tableWindows: newTableWindows};
        });
    };

    handleMessage = (event: MessageEvent<ProomMessage>): void => {
        const {data, source} = event;
        const lobbyWindow = this.lobbyIframeRef.current?.contentWindow;

        if (source === lobbyWindow) {
            this.handleLobbyMessage(data);
        }
    };

    handleTableCoordination = (message: ProomTableCoordinationMessage): void => {
        if (message.type === 'claim' && this.state.tableWindows.has(message.boardId)) {
            this.handleCloseTable(message.boardId);
        }

        if (message.type === 'restore' && message.externalId) {
            window.focus();
            this.openOrFocusTable(message.boardId, message.externalId, {skipRemoteCheck: true});
        }
    };

    broadcastUserTokenToIframes = (userToken: string): void => {
        const iframes = getIframeElements([this.lobbyIframeRef, ...Array.from(this.tableIframeRefs.values())]);

        broadcastUserToken(userToken, iframes);
        this.latestUserToken = userToken;
    };

    postInset = (inset: number): void => {
        this.lobbyIframeRef.current?.contentWindow?.postMessage({inset, type: 'proom:inset'}, PROOM_ORIGIN);
    };

    sendSetTournament = (tournamentId?: string): void => {
        this.lobbyIframeRef.current?.contentWindow?.postMessage(
            {tournamentId: tournamentId ?? null, type: 'proom:set-tournament'},
            PROOM_ORIGIN
        );
    };

    swallowUserTokenError = (): void => {
        // 401 or network error: silent. Lobby shows the expired-session UI when proom replies 401 next.
    };

    handleUserTokenRequest = (): void => {
        requestUserTokenRefresh()
            .then(userToken => {
                this.broadcastUserTokenToIframes(userToken);
            })
            .catch(this.swallowUserTokenError);
    };

    handleLobbyUnavailable = (): void => {
        this.setState({lobbyUnavailable: true});
    };

    handleLobbyRecovered = (): void => {
        this.setState({lobbyUnavailable: false});
    };

    handleRetry = (): void => {
        const reloadAttempt = this.lobbyWatcher.retry();

        this.setState({lobbyUnavailable: false, reloadAttempt});
    };

    handleIframeLoad = (): void => {
        this.lobbyWatcher.markIframeLoaded();
    };

    handleLobbyMessage = (data?: ProomMessage): void => {
        this.lobbyWatcher.markAlive();

        switch (data?.type) {
        case 'proom:resize':
            if (!this.isEmbedded && typeof data.height === 'number') {
                this.setState({lobbyHeight: data.height});
            }
            break;
        case 'proom:open-table':
            if (data.boardId) {
                this.openOrFocusTable(data.boardId, data.externalId || this.currentExternalId);
            }
            break;
        case 'proom:request-user-token':
            this.handleUserTokenRequest();
            break;
        case 'proom:navigate':
            if (typeof data.path === 'string' && (data.path === '/poker' || data.path.startsWith('/poker/'))) {
                navigateGoTo(getLinkPath(data.path));
            }
            break;
        case 'proom:require-login':
            if (data.tab === 'register') {
                openRegistration('PROOM_POKER');
            } else {
                openAuth();
            }
            break;
        default:
            break;
        }
    };

    handleTableInfo = (boardId: string, info: ProomTableInfo): void => {
        this.setState(prevState => {
            const tw = prevState.tableWindows.get(boardId);

            if (!tw) {
                return null;
            }

            const newTableWindows = new Map(prevState.tableWindows);
            newTableWindows.set(boardId, {
                ...tw,
                blinds: info.blinds,
                currency: info.currency,
                name: info.name
            });
            return {tableWindows: newTableWindows};
        });
    };

    openOrFocusTable = (
        boardId: string,
        externalId: string,
        options?: {skipRemoteCheck?: boolean}
    ): void => {
        if (!options?.skipRemoteCheck && isBoardIdClaimedRemotely(boardId)) {
            return;
        }

        if (this.state.tableWindows.has(boardId)) {
            this.handleBringToFront(boardId);
            return;
        }

        const isMobileTableView = this.getIsMobileTableView();
        this.zIndexCounter += 1;
        const zIndex = this.zIndexCounter;
        const tableCount = this.state.tableWindows.size;
        const width = WIDGET_NATURAL_WIDTH;

        const newTableWindow: TableWindow = {
            blinds: '',
            boardId,
            currency: '',
            externalId,
            name: '',
            position: isMobileTableView ? null : getDefaultTablePosition(tableCount, width),
            src: this.buildTableSrc(boardId, externalId),
            width,
            zIndex
        };

        if (isMobileTableView) {
            Array.from(this.state.tableWindows.keys()).forEach(bid => this.tableIframeRefs.delete(bid));
        }

        this.tableIframeRefs.set(boardId, React.createRef<HTMLIFrameElement>());

        this.setState(prevState => {
            if (isMobileTableView) {
                return {
                    isMobileTableView,
                    tableWindows: new Map([[boardId, newTableWindow]])
                };
            }

            const newTableWindows = new Map(prevState.tableWindows);
            newTableWindows.set(boardId, newTableWindow);
            return {isMobileTableView, tableWindows: newTableWindows};
        });
    };

    handleBringToFront = (boardId: string): void => {
        this.zIndexCounter += 1;
        const zIndex = this.zIndexCounter;
        this.setState(prevState => {
            const tw = prevState.tableWindows.get(boardId);

            if (!tw) {
                return null;
            }

            const newTableWindows = new Map(prevState.tableWindows);
            newTableWindows.set(boardId, {...tw, zIndex});
            return {tableWindows: newTableWindows};
        });
    };

    handleCloseTable = (boardId: string): void => {
        this.tableIframeRefs.delete(boardId);
        this.setState(prevState => {
            const newTableWindows = new Map(prevState.tableWindows);
            newTableWindows.delete(boardId);
            return {tableWindows: newTableWindows};
        });
    };

    handleTablePositionChange = (boardId: string, position: TablePosition): void => {
        this.setState(prevState => {
            const tw = prevState.tableWindows.get(boardId);

            if (!tw) {
                return null;
            }

            const newTableWindows = new Map(prevState.tableWindows);
            newTableWindows.set(boardId, {
                ...tw,
                position: clampTablePosition(position, tw.width)
            });
            return {tableWindows: newTableWindows};
        });
    };

    handleTableSizeChange = (boardId: string, width: number): void => {
        const clampedWidth = clampWidgetWidth(width);

        this.setState(prevState => {
            const tw = prevState.tableWindows.get(boardId);

            if (!tw) {
                return null;
            }

            const newTableWindows = new Map(prevState.tableWindows);
            newTableWindows.set(boardId, {
                ...tw,
                position: tw.position ? clampTablePosition(tw.position, clampedWidth) : tw.position,
                width: clampedWidth
            });
            return {tableWindows: newTableWindows};
        });
    };

    handleTableTransfer = (oldBoardId: string, newBoardId: string): void => {
        const iframeRef = this.tableIframeRefs.get(oldBoardId);

        if (iframeRef) {
            this.tableIframeRefs.delete(oldBoardId);
            this.tableIframeRefs.set(newBoardId, iframeRef);
        }

        this.setState(prevState => {
            const tw = prevState.tableWindows.get(oldBoardId);

            if (!tw) {
                return null;
            }

            const newTableWindows = new Map(prevState.tableWindows);
            newTableWindows.delete(oldBoardId);
            newTableWindows.set(newBoardId, {
                ...tw,
                blinds: '',
                boardId: newBoardId,
                currency: '',
                name: '',
                src: this.buildTableSrc(newBoardId, tw.externalId)
            });
            return {tableWindows: newTableWindows};
        });
    };

    handleUserTokenRefreshed = (userToken: string): void => {
        this.broadcastUserTokenToIframes(userToken);
    };

    renderUnavailableStub(): React.ReactNode {
        const minHeight = this.isEmbedded ? EMBEDDED_LOBBY_HEIGHT : DEFAULT_LOBBY_HEIGHT;

        return <div style={{...styles.unavailableCard, minHeight}}>
            <p style={styles.unavailableText}>{I18n.t('poker.proom_unavailable')}</p>
            <button
                onClick={this.handleRetry}
                style={styles.unavailableReloadButton}
                type="button"
            >
                {I18n.t('poker.try_again')}
            </button>
        </div>;
    }

    renderLobby(): React.ReactNode {
        if (this.isCoolOffActive) {
            return <PokerCoolOffStub
                embedded={this.isEmbedded}
                endDate={this.user?.cool_off?.end_date || ''}
            />;
        }

        const {isMobileTableView, lobbyHeight, lobbyUnavailable, reloadAttempt} = this.state;
        const cardClassName = isMobileTableView
            ? 'poker-lobby-card poker-lobby-card--mobile'
            : 'poker-lobby-card';
        const iframeStyle = lobbyUnavailable
            ? {...styles.lobbyIframeBase, ...styles.lobbyIframeHidden}
            : {...styles.lobbyIframeBase, height: lobbyHeight};

        return <div className={cardClassName} style={styles.lobbyCard}>
            <iframe
                aria-hidden={lobbyUnavailable}
                id="proom-lobby"
                key={`proom-lobby-${this.lobbyMode}-${reloadAttempt}`}
                onLoad={this.handleIframeLoad}
                ref={this.lobbyIframeRef}
                scrolling="no"
                src={this.lobbyIframeSrc}
                style={iframeStyle}
                title="proom-lobby"
            />
            {lobbyUnavailable && this.renderUnavailableStub()}
        </div>;
    }

    renderTableWindows(): React.ReactNode {
        const {tableWindows, isMobileTableView} = this.state;

        if (tableWindows.size === 0) {
            return null;
        }

        const windows = Array.from(tableWindows.values());
        const windowsToRender = isMobileTableView ? [windows[windows.length - 1]] : windows;

        return windowsToRender.map(tw => {
            const iframeRef = this.tableIframeRefs.get(tw.boardId)!;
            return ReactDOM.createPortal(
                <ProomTableWindowItem
                    iframeRef={iframeRef}
                    isMobile={isMobileTableView}
                    session={this.proomSession}
                    tableWindow={tw}
                    tableSrc={tw.src}
                    onBringToFront={this.handleBringToFront}
                    onClose={this.handleCloseTable}
                    onInfo={this.handleTableInfo}
                    onLeave={this.handleCloseTable}
                    onPositionChange={this.handleTablePositionChange}
                    onSizeChange={this.handleTableSizeChange}
                    onTableTransfer={this.handleTableTransfer}
                    onUserTokenRefreshed={this.handleUserTokenRefreshed}
                />,
                document.body,
                tw.boardId
            );
        });
    }

    render(): React.ReactNode {
        const lobbyAndWindows = <>
            {this.renderLobby()}
            {!this.isCoolOffActive && this.renderTableWindows()}
        </>;

        if (this.isEmbedded) {
            return (
                <AsideItem
                    header={
                        <div className="aside-item-heading__title">
                            <h3>{I18n.t('poker.title')}</h3>
                        </div>
                    }
                    name="proom-poker"
                    itemClassName="proom-poker-aside"
                >
                    <div style={styles.embeddedAsideInner}>
                        <style>{widgetShellStyles}</style>
                        {lobbyAndWindows}
                    </div>
                </AsideItem>
            );
        }

        return (
            <div className="poker-page" style={styles.page}>
                <style>{widgetShellStyles}</style>
                {lobbyAndWindows}
            </div>
        );
    }
}

export default withRouter(ProomPoker);

const TABLE_BG = '#0f0f0f';
const styles: Record<string, React.CSSProperties> = {
    embeddedAsideInner: {position: 'relative'},
    lobbyCard: {position: 'relative'},
    lobbyIframeBase: {background: TABLE_BG, border: 'none', display: 'block', minHeight: 200, width: '100%'},
    lobbyIframeHidden: {height: '100%', left: 0, opacity: 0, pointerEvents: 'none', position: 'absolute', top: 0},
    page: {},
    unavailableCard: {
        alignItems: 'center',
        background: TABLE_BG,
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        justifyContent: 'center',
        minHeight: DEFAULT_LOBBY_HEIGHT,
        padding: 24,
        textAlign: 'center'
    },
    unavailableReloadButton: {
        background: '#fe9400',
        border: 'none',
        borderRadius: 3,
        color: '#000',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: '0.04em',
        padding: '10px 20px',
        textTransform: 'uppercase'
    },
    unavailableText: {color: '#c5c5c5', fontSize: 16, margin: 0}
};
